import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import TableWithLazyLoading from 'shared/components/Grid/TableWithLazyLoading';
import GridNav, {
  DefaultGridNav,
  GridNavHandle,
} from 'shared/components/Grid/GridNav';
import { ConfirmationDialog } from 'shared/components';
import {
  FilterGroups,
  ReportAPIResponse,
  ReportsCountResponse,
  VariablesForCountFilter,
  getReportsApi,
  getReportsCount,
  deleteReportApi,
  getAllReports,
  AllReportYears,
  disableProgramApi,
  ProgramGroup,
} from './api';
import {
  ReportGroups,
  defaultReportFilters,
  defaultReportQuickFilters,
  getReportGroupName,
  getReportGroups,
  reportFilterKeys,
  reportQuickFilterKeys,
} from './utils';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import ReportFilters from './ReportFilters';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useRouterQueryString,
  useSetQueryParams,
} from 'shared/auth/hooks/useQueryString';
import {
  getGroupItemRender,
  getReportGroupKey,
  isV3Report,
  reportHeaders,
  reportingTypeHeader,
  ReportsTable,
  reportVisibilityHeader,
} from './ReportGroup';
import { RenderEmptyReportState } from './RenderEmptyReportState';
import ReportAddNew from './ReportAddNew';
import { Program } from '../Reports/NewReportDialog';
import { getPrograms } from './NewReport/api';
import { NPOUser, getAllNPOUsers } from './NewProgram/api';
import OldReport from './OldReport';
import ImpactSnapshotModal from './ImpactSnapshot';
import { GridTableRowProps } from 'shared/components/Grid/GridTableRow';
import MoreItem from './MoreItem';
import MoreItemModal from './MoreItem/MoreItemModal';
import { getProgramNameUrl } from '../ReportBuilder/Components/Budget/utils';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';

const showFeedbackStatuses: FeedbackStatus[] = [
  'Awaiting TI Feedback',
  'Awaiting TI Verification',
  'Awaiting Funder Feedback',
  'Awaiting Funder Verification',
  'TI Feedback Provided',
  'Funder Feedback Provided',
] as const;
type Header = GridTableRowProps<ReportsTable>['headers'][number];

const RESULT_COUNT = 10;
export const StyledTableGridDiv = styled.div`
  .report-header {
    padding: ${SPACERS.medium};
    background-color: ${COLORS.grayLight};
    .show-overview-button {
      padding-left: ${SPACERS.medium};
      padding-right: ${SPACERS.medium};
      background-color: ${COLORS.whiteBase};
      svg.icon {
        width: 24px;
        height: 24px;
        margin-right: ${SPACERS.tiny};
      }
    }
  }
  .table-grid-wrapper {
    display: flex;
    background-color: ${COLORS.whiteBase};
    .table-grid {
      flex: 1;
    }
  }
  .absolute {
    position: absolute;
    z-index: 999;
    top: 50%;
    left: 50%;
    background-color: ${COLORS.whiteBase};
    padding: 20px;
    border-radius: 100px;
    box-shadow: 0px 4px 8px rgba(00, 00, 00, 0.5);
  }
  .center {
    text-align: center;
  }
`;

const FILTER_PARAM_MAP: Record<typeof reportFilterKeys[number], string> = {
  Program: 'program',
  Year: 'year',
  Donor: 'donor',
  Manager: 'manager',
};

const PARAM_TO_FILTER: Record<string, typeof reportFilterKeys[number]> = {
  program: 'Program',
  year: 'Year',
  donor: 'Donor',
  manager: 'Manager',
};

const getFilterFromParams = (
  search: string,
): {
  filter: typeof reportFilterKeys[number];
  groupValue: string | undefined;
} => {
  const params = new URLSearchParams(search);
  for (const key of Object.keys(PARAM_TO_FILTER)) {
    if (params.has(key)) {
      return {
        filter: PARAM_TO_FILTER[key],
        groupValue: params.get(key) || undefined,
      };
    }
  }
  return { filter: reportFilterKeys[0], groupValue: undefined };
};

const ReportsBeta: React.FC = () => {
  const client = useClient();
  const { push } = useHistory();
  const location = useLocation();
  const queryParams = useRouterQueryString();
  const setQueryParams = useSetQueryParams();
  const reportVisibility = useFeatureFlags('reportVisibility');
  const programProfilingEnabled = useFeatureFlags('programProfiling');

  const initialParams = useMemo(() => getFilterFromParams(location.search), []);

  const gridNavRef = useRef<
    GridNavHandle<ReturnType<typeof getReportGroups>[number]>
  >(null);
  const [openOldReport, setOpenOldReport] = useState<ReportsTable | undefined>(
    undefined,
  );
  const [snapshotReport, setSnapshotReport] = useState<
    ReportsTable | undefined
  >(undefined);
  const [searchText, setSearchText] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<FilterGroups | undefined>(
    undefined,
  );
  const [filter, setFilter] = useState<typeof reportFilterKeys[number]>(
    initialParams.filter,
  );
  const [quickFilter, setQuickFilter] = useState<
    typeof reportQuickFilterKeys[number]
  >(reportQuickFilterKeys[0]);
  const [reportIdToDelete, setReportIdToDelete] = useState<
    string | number | undefined
  >();
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  const [programToDelete, setProgramToDelete] = useState<string | undefined>();

  const [more, setMore] = useState<
    { row: ReportsTable; index: number } | undefined
  >(undefined);

  const [requestState, makeRequest] = useGraphQLRequest<{
    reportsTodo: ReportAPIResponse[];
  }>();
  const [requestAllReportsState, makeAllReportsRequest] = useGraphQLRequest<
    AllReportYears
  >();
  const [countsRequestState, makeCountsRequest] = useGraphQLRequest<
    ReportsCountResponse
  >();
  const [
    programsList,
    makeProgramsListRequest,
    setProgramsList,
  ] = useGraphQLRequest<{
    allPrograms: Program[];
  }>();
  const [allNPOUsers, makeAllNPOUsersRequest] = useGraphQLRequest<{
    allUsers: NPOUser[];
  }>();

  const programIdByName = useMemo(() => {
    const map: Record<string, number> = {};
    if (programsList.type === 'REQUEST_SUCCESS') {
      programsList.data.allPrograms.forEach(p => {
        map[p.name] = p.Id;
      });
    }
    return map;
  }, [programsList]);

  const programNameById = useMemo(() => {
    const map: Record<string, string> = {};
    if (programsList.type === 'REQUEST_SUCCESS') {
      programsList.data.allPrograms.forEach(p => {
        map[String(p.Id)] = p.name;
      });
    }
    return map;
  }, [programsList]);

  const handleOldReportClose = useCallback(() => {
    setOpenOldReport(undefined);
  }, [setOpenOldReport]);

  const reloadProgramList = useCallback(() => {
    makeProgramsListRequest(getPrograms(client!));
  }, [setProgramsList]);

  const updateQueryParams = useCallback(
    (filterKey: typeof reportFilterKeys[number], value?: string | number) => {
      // Clear all filter params first, then set the active one (empty string
      // preserves presence so `getFilterFromParams` can detect it).
      const updates: Record<string, string | number | null> = {};
      Object.values(FILTER_PARAM_MAP).forEach(p => {
        updates[p] = null;
      });
      updates[FILTER_PARAM_MAP[filterKey]] = value != null ? value : '';
      setQueryParams(updates);
    },
    [setQueryParams],
  );

  const setReportIdParam = useCallback(
    (reportId?: string | number) => {
      setQueryParams({ reportId: reportId != null ? reportId : null });
    },
    [setQueryParams],
  );

  const handleFilterChange = useCallback(
    (newFilter: typeof reportFilterKeys[number]) => {
      setFilter(newFilter);
      updateQueryParams(newFilter);
    },
    [updateQueryParams],
  );

  const handleGroupClick = useCallback(
    (item: ReportGroups, index: number, extra: VariablesForCountFilter) => {
      if (!item || !extra.filter) return;
      const value = getReportGroupName(item);
      const urlValue =
        extra.filter === 'Program'
          ? programIdByName[String(value)] ?? value
          : value;
      updateQueryParams(filter, urlValue);
      setGroupFilter(s => {
        if (s && getReportGroupName(s[0]) === value) return s;
        return [
          {
            [extra.filter.toLowerCase()]: value,
          },
        ];
      });
    },
    [setGroupFilter, filter, updateQueryParams, programIdByName],
  );

  useEffect(() => {
    if (!client) return;
    makeProgramsListRequest(getPrograms(client));
    makeAllNPOUsersRequest(getAllNPOUsers(client));
  }, [client]);

  const defaultProgramName =
    filter === 'Program'
      ? (gridNavRef.current?.getActiveItem as () =>
          | ProgramGroup
          | undefined)?.()?.program
      : undefined;

  const handleReportCountLoad = useCallback(() => {
    // const group =
    //   countsRequestState.data && getReportGroups(countsRequestState.data);
    // it stops the pull of group list if `filter` hasn't changed
    // if (group && Object.keys(group[0])[0] === filter.toLowerCase()) return;
    makeCountsRequest(
      getReportsCount(client!, { searchText, filter, quickFilter }),
    );
  }, [filter, searchText, quickFilter]);

  useEffect(() => {
    handleReportCountLoad();
    makeAllReportsRequest(getAllReports(client!));
  }, [handleReportCountLoad]);

  const handleLoadMore = useCallback(
    (pageNumber: number, filters: VariablesForCountFilter) => {
      const first = RESULT_COUNT;
      const offset = (pageNumber - 1) * RESULT_COUNT;
      groupFilter &&
        makeRequest(
          getReportsApi(
            client!,
            {
              ...filters,
              first,
              offset,
              groupFilter,
            },
            true,
          ),
        );
    },
    [groupFilter, makeRequest],
  );
  const handleReportClick = useCallback(
    (_e: unknown, row: ReportsTable) => {
      if (isV3Report(row)) {
        setOpenOldReport(row);
        return;
      }
      if (
        row.reportingType === 'LIGHT' ||
        row.reportingType === 'SUPER_LIGHT'
      ) {
        setSnapshotReport(row);
        setReportIdParam(row.dbId);
        return;
      }
      const reportBuilderLink = `/npo/report-builder/${getProgramNameUrl(
        defaultProgramName,
      )}/${row.dbId}`;

      const feedbackLink = `/npo/feedback/${row.dbId}`;

      push(
        showFeedbackStatuses.includes(row.status)
          ? feedbackLink
          : reportBuilderLink,
      );
    },
    [push, setOpenOldReport, defaultProgramName, setReportIdParam],
  );

  // Restore snapshot modal from ?reportId= on refresh/deep link
  useEffect(() => {
    const reportIdParam = queryParams.get('reportId');
    if (!reportIdParam || snapshotReport) return;
    const data = requestAllReportsState.data;
    if (!data) return;
    const dbId = parseInt(reportIdParam, 10);
    if (Number.isNaN(dbId)) return;
    for (const key of Object.keys(data)) {
      if (key === 'programs') continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const entry = (data as any)[key];
      const found = entry?.reports?.find(
        (r: { dbId: number }) => r.dbId === dbId,
      );
      if (found) {
        setSnapshotReport(({
          ...found,
          id: found.dbId,
        } as unknown) as ReportsTable);
        return;
      }
    }
  }, [requestAllReportsState.data]);

  const handleReloadReportPage = useCallback(() => {
    reloadProgramList();
    handleReportCountLoad();
  }, [reloadProgramList, handleReportCountLoad]);

  const handleDeleteClick = (reportId: string) => {
    if (reportId) {
      setReportIdToDelete(parseInt(reportId));
    }
  };

  const handleRunDelete = async () => {
    if (!reportIdToDelete || isDeleting) return;

    const reportId =
      typeof reportIdToDelete === 'string'
        ? parseInt(reportIdToDelete)
        : reportIdToDelete;

    setDeleteError(undefined);
    setIsDeleting(true);

    try {
      const result = await deleteReportApi(client!, reportId);
      // TIPD-4096: Handle GraphQL errors in result.errors
      if (result.errors && result.errors.length > 0) {
        const errorMessage =
          result.errors[0]?.message || 'Unable to delete report.';
        setDeleteError(errorMessage);
        return;
      }
      setReportIdToDelete(undefined);
      await handleReloadReportPage();
    } catch (error) {
      // TIPD-4096: Handle network/exception errors
      let errorMessage =
        (error as Error)?.message ||
        'Unable to delete report. Please try again.';
      // Clean up GraphQL error prefix for cleaner UX
      errorMessage = errorMessage.replace(/^GraphQL error:\s*/i, '');
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const req: RequestState<ReportsTable[]> = useMemo(
    () => ({
      ...requestState,
      data: (requestState?.data?.reportsTodo ?? []).map(d => ({
        ...d,
        id: d.dbId,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      })) as any,
    }),
    [requestState?.data],
  );

  const selectedFilters: VariablesForCountFilter = useMemo(
    () => ({
      searchText,
      filter,
      quickFilter,
    }),
    [searchText, filter, quickFilter],
  );

  const initialFilterKeys = useMemo(() => {
    const filters = defaultReportFilters();
    for (const key of reportFilterKeys) {
      filters[key].selected = key === initialParams.filter;
    }
    return filters;
  }, []);

  const EmptyState = useMemo(
    () => () => (
      <RenderEmptyReportState
        allNPOUsers={allNPOUsers}
        programsList={programsList}
        searchText={searchText}
        defaultProgramName={defaultProgramName}
        reloadProgramList={reloadProgramList}
      />
    ),
    [allNPOUsers, programsList, searchText, defaultProgramName],
  );

  const groupCounts = getReportGroups(countsRequestState.data);

  const initialActiveIndex = useMemo(() => {
    if (!initialParams.groupValue || groupCounts.length === 0) return 0;
    const resolvedValue =
      initialParams.filter === 'Program'
        ? programNameById[initialParams.groupValue] ?? initialParams.groupValue
        : initialParams.groupValue;
    const idx = groupCounts.findIndex(
      item =>
        String(getReportGroupName(item)).toLowerCase() ===
        resolvedValue.toLowerCase(),
    );
    return idx >= 0 ? idx : 0;
  }, [
    groupCounts,
    initialParams.groupValue,
    initialParams.filter,
    programNameById,
  ]);
  const isEmpty =
    countsRequestState.type === 'REQUEST_SUCCESS' && groupCounts.length === 0;

  const headerWithCloneBtn: Header[] = useMemo(
    () =>
      [
        ...reportHeaders,
        programProfilingEnabled ? reportingTypeHeader : undefined,
        reportVisibility ? reportVisibilityHeader : undefined,
        {
          text: '',
          width: '35px',
          Render: (item: { row: ReportsTable; index: number }) => (
            <MoreItem item={item} handleMore={setMore} />
          ),
        },
      ].filter((h): h is Header => h !== undefined),
    [setMore, reportVisibility, programProfilingEnabled],
  );

  return (
    <StyledTableGridDiv>
      <div className="report-header">
        <ReportAddNew
          defaultProgramName={defaultProgramName}
          programsList={programsList}
          allNPOUsers={allNPOUsers}
          countsRequestState={countsRequestState}
          reloadProgramList={reloadProgramList}
          handleReportCountLoad={handleReportCountLoad}
        />
        <ReportFilters
          searchPlaceholder="Search Reports"
          filterKeys={initialFilterKeys}
          quickFilterKeys={defaultReportQuickFilters()}
          handleFilter={handleFilterChange}
          handleQuickFilter={setQuickFilter}
          handleSearchText={setSearchText}
        />
      </div>
      <div className="table-grid-wrapper">
        {countsRequestState.type === 'REQUEST_SUCCESS' ? (
          <GridNav
            ref={gridNavRef}
            items={groupCounts}
            groupName={filter}
            initialActiveIndex={initialActiveIndex}
            getKey={getReportGroupKey}
            onClick={handleGroupClick}
            renderItem={(item, index, isActive, extra) => {
              const programName = getReportGroupName(item).toString();
              const programId = programIdByName[programName];
              return getGroupItemRender(
                item,
                index,
                isActive,
                extra,
                programId,
                name => {
                  setProgramToDelete(name);
                },
              );
            }}
            extraData={{
              searchText,
              filter,
              quickFilter,
              programsList,
              allNPOUsers,
              reloadProgramList: handleReloadReportPage,
            }}
          />
        ) : (
          <DefaultGridNav groupName={filter} />
        )}
        <div className="table-grid">
          {isEmpty ? (
            <EmptyState />
          ) : (
            <TableWithLazyLoading
              headers={headerWithCloneBtn}
              resultCount={RESULT_COUNT}
              filters={selectedFilters}
              onRowClick={handleReportClick}
              // onDelete={handleDeleteClick}
              requestState={req}
              loadMore={handleLoadMore}
              RenderEmptyState={EmptyState}
            />
          )}
        </div>
      </div>
      {openOldReport && (
        <OldReport report={openOldReport} handleClose={handleOldReportClose} />
      )}
      {snapshotReport && (
        <ImpactSnapshotModal
          report={snapshotReport}
          onClose={() => {
            setSnapshotReport(undefined);
            setReportIdParam(undefined);
          }}
        />
      )}
      <ConfirmationDialog
        openDialog={reportIdToDelete}
        onCloseDialog={() => {
          if (!isDeleting) {
            setReportIdToDelete(undefined);
            setDeleteError(undefined);
          }
        }}
        actionButtonOne={() => {
          if (deleteError) {
            setReportIdToDelete(undefined);
            setDeleteError(undefined);
          } else {
            handleRunDelete();
          }
        }}
        actionButtonTwo={() => {
          setReportIdToDelete(undefined);
          setDeleteError(undefined);
        }}
        textButtonOne={deleteError ? 'Close' : 'Yes'}
        textButtonTwo={deleteError ? undefined : 'No'}
        title={
          deleteError
            ? `Unable to delete this report. ${deleteError}`
            : 'Are you sure you want to delete the report?'
        }
        header={deleteError ? 'Error' : 'Confirmation is required'}
        isLoading={isDeleting}
      />
      <ConfirmationDialog
        openDialog={!!programToDelete}
        onCloseDialog={() => setProgramToDelete(undefined)}
        actionButtonOne={async () => {
          const matched = programsList?.data?.allPrograms.find(
            p => p.name === programToDelete,
          );
          if (matched?.Id) {
            await makeRequest(disableProgramApi(client!, matched?.Id));
            await handleReloadReportPage();
            setTimeout(async () => {
              // A second reload ensures there is no cache remaining on the comunication between the apollp client and the server
              await handleReloadReportPage();
            }, 2000);
          }
          setProgramToDelete(undefined);
        }}
        actionButtonTwo={() => setProgramToDelete(undefined)}
        textButtonOne="Yes"
        textButtonTwo="Cancel"
        title={`Delete "${programToDelete}"?`}
        header="Confirm Program Deletion"
      />
      {more && (
        <MoreItemModal
          report={more.row}
          index={more.index}
          open={true}
          reportsState={requestAllReportsState}
          programsList={programsList}
          handleClose={() => setMore(undefined)}
          handleReloadReportPage={handleReloadReportPage}
          onDelete={handleDeleteClick}
        />
      )}
    </StyledTableGridDiv>
  );
};

export default ReportsBeta;
